/**
 * Alpine.js directive for auto-fitting text to container width.
 * Uses binary search to find the largest font-size that fits.
 *
 * Usage: x-textfit="{ min: 24, max: 72, maxLines: 2 }"
 *
 * When maxLines > 1, the algorithm switches to height-based fitting
 * if text would wrap at minimum font size, allowing text-balance
 * to work properly with wrapped text.
 */
export default function (Alpine) {
  Alpine.directive('textfit', (el, { expression }, { evaluate, cleanup }) => {
    const config = expression ? evaluate(expression) : {};
    const min = config.min || 16;
    const max = config.max || 120;
    const maxLines = config.maxLines || 1;

    let rafId = null;
    let isFitting = false;
    let hasInitialFit = false;
    let lastParentWidth = null;

    /**
     * Count lines by comparing element height to line height.
     * Finds the text span inside flex containers for accurate measurement.
     */
    function getLineCount() {
      const textEl = el.querySelector('span') || el;
      const fontSize = parseFloat(getComputedStyle(el).fontSize);
      const lineHeight = parseFloat(getComputedStyle(textEl).lineHeight);
      // Use actual lineHeight if available, otherwise assume line-height: 1
      const effectiveLineHeight = isNaN(lineHeight) ? fontSize : lineHeight;
      return Math.ceil(textEl.scrollHeight / effectiveLineHeight);
    }

    /**
     * Binary search for largest font size that keeps text within targetLines.
     */
    function binarySearchLines(targetLines) {
      let lo = min, hi = max;

      while (hi - lo > 1) {
        const mid = Math.floor((lo + hi) / 2);
        el.style.fontSize = `${mid}px`;

        if (getLineCount() <= targetLines) {
          lo = mid; // Can go bigger
        } else {
          hi = mid; // Too many lines
        }
      }

      el.style.fontSize = `${lo}px`;
    }

    /**
     * Binary search for largest font size that fits on single line.
     * Keeps text-wrap: nowrap set to prevent text-balance from wrapping.
     */
    function binarySearchWidth(availableWidth) {
      const textEl = el.querySelector('span') || el;
      let lo = min, hi = max;

      // Measure text element's natural width, not flex container
      const origWidth = textEl.style.width;
      textEl.style.whiteSpace = 'nowrap';
      textEl.style.width = 'max-content';

      while (hi - lo > 1) {
        const mid = Math.floor((lo + hi) / 2);
        el.style.fontSize = `${mid}px`;
        if (textEl.scrollWidth <= availableWidth) {
          lo = mid;
        } else {
          hi = mid;
        }
      }

      el.style.fontSize = `${lo}px`;
      // Keep white-space: nowrap to override text-balance in single-line mode
      textEl.style.width = origWidth;
    }

    /**
     * Calculate actual available width for text, accounting for
     * siblings (like SVG icons), gaps, and padding.
     */
    function getAvailableTextWidth() {
      const style = getComputedStyle(el);
      const paddingLeft = parseFloat(style.paddingLeft) || 0;
      const paddingRight = parseFloat(style.paddingRight) || 0;
      const gap = parseFloat(style.gap) || parseFloat(style.columnGap) || 0;

      // Sum width of all non-text siblings (e.g., SVG icons)
      let siblingsWidth = 0;
      for (const child of el.children) {
        if (child.tagName !== 'SPAN') {
          siblingsWidth += child.getBoundingClientRect().width;
        }
      }

      // Available = parent width - padding - siblings - gaps - safety margin
      // Cap at viewport width minus page margins to prevent overflow
      const siblingCount = el.children.length - 1; // excluding the text span
      const totalGap = siblingCount > 0 ? gap * siblingCount : 0;
      const pageMargin = 56; // Account for page padding (px-7 = 28px * 2)
      const safetyMargin = 64; // Extra breathing room for em-scaled siblings (SVG grows with font)
      const parentWidth = Math.min(el.parentElement.clientWidth, window.innerWidth - pageMargin);

      return parentWidth - paddingLeft - paddingRight - siblingsWidth - totalGap - safetyMargin;
    }

    function fit() {
      if (isFitting) return;
      isFitting = true;

      try {
        // Check if parent width actually changed (prevents ResizeObserver feedback loop)
        // The loop occurs because: font change → em-scaled siblings resize → availableWidth changes
        // But parent width stays constant, so we use that as the stability check
        const currentParentWidth = el.parentElement.clientWidth;
        if (lastParentWidth !== null && Math.abs(currentParentWidth - lastParentWidth) < 1) {
          return; // Parent didn't resize, skip re-fit
        }
        lastParentWidth = currentParentWidth;

        const availableWidth = getAvailableTextWidth();
        const textEl = el.querySelector('span') || el;
        const originalWhiteSpace = el.style.whiteSpace;

        // Reset any inline whiteSpace from previous fit (critical for mode transitions)
        textEl.style.whiteSpace = '';

        // Step 1: Check if text fits on one line at min size
        // Use max-content to get natural text width (block elements stretch to container)
        el.style.whiteSpace = 'nowrap';
        el.style.fontSize = `${min}px`;
        const originalWidth = textEl.style.width;
        textEl.style.width = 'max-content';
        const singleLineWidth = textEl.scrollWidth;
        textEl.style.width = originalWidth;

        if (singleLineWidth <= availableWidth || maxLines === 1) {
          // SINGLE-LINE MODE: Use width-based binary search
          binarySearchWidth(availableWidth);
          el.style.whiteSpace = originalWhiteSpace;
        } else {
          // MULTI-LINE MODE: Allow wrapping, find max font for ≤ maxLines
          el.style.whiteSpace = originalWhiteSpace;
          binarySearchLines(maxLines);
        }
      } finally {
        isFitting = false;
        // Mark element as ready to reveal it (CSS hides [x-textfit]:not([data-textfit-ready]))
        if (!hasInitialFit) {
          el.setAttribute('data-textfit-ready', '');
          hasInitialFit = true;
        }
      }
    }

    function scheduleFit() {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        fit();
      });
    }

    // Watch for size changes via both ResizeObserver and window resize
    const observer = new ResizeObserver(scheduleFit);
    observer.observe(el.parentElement);

    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(fit, 100);
    };
    window.addEventListener('resize', handleResize);

    fit();

    // Cleanup when element is removed
    cleanup(() => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
      if (rafId) cancelAnimationFrame(rafId);
    });
  });
}
